import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { Wand2, Loader2, CheckCircle, XCircle, Eye, Trash2, RefreshCw } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNoIndex } from "@/hooks/useCanonicalTag";

interface QuizOptions {
  a: string;
  b: string;
  c: string;
  d: string;
}

interface Quiz {
  id: string;
  question: string;
  options: QuizOptions;
  correct_answer: string;
  explanation: string;
  lesson_content: string;
  category: string;
  difficulty: string;
  status: string;
  is_active: boolean;
  points: number;
  created_at: string;
}

const categoryLabels: Record<string, string> = {
  sql: "SQL",
  python: "Python",
  statistics: "Estatística",
  bi: "Business Intelligence",
  kpis: "KPIs e Métricas",
  google_analytics: "Google Analytics",
  google_tag_manager: "Google Tag Manager",
  looker_studio: "Looker Studio",
  power_bi: "Power BI",
  data_engineering: "Data Engineering",
  machine_learning: "Machine Learning",
  excel_advanced: "Excel Avançado",
  cloud_aws: "Cloud AWS",
  cloud_azure: "Cloud Azure",
  cloud_gcp: "Cloud GCP"
};

const difficultyLabels: Record<string, string> = {
  beginner: "Iniciante",
  intermediate: "Intermediário",
  advanced: "Avançado"
};

export default function QuizGenerator() {
  useNoIndex();
  const navigate = useNavigate();
  const user = useUser();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const [category, setCategory] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [quantity, setQuantity] = useState("5");
  
  const [draftQuizzes, setDraftQuizzes] = useState<Quiz[]>([]);
  const [approvedQuizzes, setApprovedQuizzes] = useState<Quiz[]>([]);
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  useEffect(() => {
    checkAdminAndFetchQuizzes();
  }, [user]);

  const checkAdminAndFetchQuizzes = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("is_admin")
      .eq("id", user.id)
      .single();

    if (!profile?.is_admin) {
      navigate("/candidate/dashboard");
      return;
    }

    setIsAdmin(true);
    await fetchQuizzes();
    setLoading(false);
  };

  const fetchQuizzes = async () => {
    const { data, error } = await supabase
      .from("quizzes")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching quizzes:", error);
      return;
    }

    const quizzes = (data || []).map(q => ({
      ...q,
      options: q.options as unknown as QuizOptions
    })) as Quiz[];
    setDraftQuizzes(quizzes.filter(q => q.status === "draft"));
    setApprovedQuizzes(quizzes.filter(q => q.status === "approved"));
  };

  const handleGenerate = async () => {
    if (!category || !difficulty) {
      toast({
        title: "Campos obrigatórios",
        description: "Selecione categoria e dificuldade",
        variant: "destructive"
      });
      return;
    }

    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-quiz-questions", {
        body: { category, difficulty, quantity: parseInt(quantity) }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Questões geradas!",
        description: `${data.count} questões criadas como rascunho para revisão.`
      });

      await fetchQuizzes();
    } catch (error) {
      console.error("Generation error:", error);
      toast({
        title: "Erro ao gerar questões",
        description: error instanceof Error ? error.message : "Tente novamente",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (quizId: string) => {
    const { error } = await supabase
      .from("quizzes")
      .update({ status: "approved", is_active: true })
      .eq("id", quizId);

    if (error) {
      toast({
        title: "Erro ao aprovar",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    toast({ title: "Questão aprovada!" });
    await fetchQuizzes();
  };

  const handleReject = async (quizId: string) => {
    const { error } = await supabase
      .from("quizzes")
      .update({ status: "rejected", is_active: false })
      .eq("id", quizId);

    if (error) {
      toast({
        title: "Erro ao rejeitar",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    toast({ title: "Questão rejeitada" });
    await fetchQuizzes();
  };

  const handleDelete = async (quizId: string) => {
    const { error } = await supabase
      .from("quizzes")
      .delete()
      .eq("id", quizId);

    if (error) {
      toast({
        title: "Erro ao excluir",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    toast({ title: "Questão excluída" });
    await fetchQuizzes();
  };

  const handleApproveAll = async () => {
    const { error } = await supabase
      .from("quizzes")
      .update({ status: "approved", is_active: true })
      .eq("status", "draft");

    if (error) {
      toast({
        title: "Erro ao aprovar todas",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    toast({ title: "Todas as questões foram aprovadas!" });
    await fetchQuizzes();
  };

  if (loading) {
    return (
      <CandidateLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </CandidateLayout>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <CandidateLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Gerador de Questões com IA</h1>
          <p className="text-muted-foreground">
            Gere questões automaticamente e revise antes de publicar
          </p>
        </div>

        {/* Generator Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              Gerar Novas Questões
            </CardTitle>
            <CardDescription>
              Use IA para criar questões de múltipla escolha com explicações e conteúdo educacional
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Dificuldade</Label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(difficultyLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Quantidade</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full"
                />
              </div>

              <div className="flex items-end">
                <Button 
                  onClick={handleGenerate} 
                  disabled={generating}
                  className="w-full"
                >
                  {generating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span className="hidden sm:inline">Gerando...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="mr-2 h-4 w-4" />
                      <span className="hidden sm:inline">Gerar Questões</span>
                      <span className="sm:hidden">Gerar</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Draft/Approved */}
        <Tabs defaultValue="draft" className="space-y-4">
          <TabsList>
            <TabsTrigger value="draft" className="flex items-center gap-2">
              Rascunhos
              {draftQuizzes.length > 0 && (
                <Badge variant="secondary">{draftQuizzes.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved" className="flex items-center gap-2">
              Aprovadas
              <Badge variant="secondary">{approvedQuizzes.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="draft">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg sm:text-xl">Questões Pendentes de Revisão</CardTitle>
                  <CardDescription className="text-sm">
                    Revise e aprove as questões geradas pela IA
                  </CardDescription>
                </div>
                {draftQuizzes.length > 0 && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchQuizzes}>
                      <RefreshCw className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Atualizar</span>
                    </Button>
                    <Button size="sm" onClick={handleApproveAll}>
                      <CheckCircle className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Aprovar Todas</span>
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {draftQuizzes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhuma questão pendente. Gere novas questões acima.
                  </p>
                ) : isMobile ? (
                  // Mobile: Cards view
                  <div className="space-y-3">
                    {draftQuizzes.map((quiz) => (
                      <Card key={quiz.id} className="p-4">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {categoryLabels[quiz.category] || quiz.category}
                          </Badge>
                          <Badge 
                            variant={
                              quiz.difficulty === "beginner" ? "secondary" :
                              quiz.difficulty === "intermediate" ? "default" : "destructive"
                            }
                            className="text-xs"
                          >
                            {difficultyLabels[quiz.difficulty] || quiz.difficulty}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">{quiz.points} pts</Badge>
                        </div>
                        <p className="text-sm line-clamp-2 mb-3">{quiz.question}</p>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedQuiz(quiz);
                              setPreviewOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600"
                            onClick={() => handleApprove(quiz.id)}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleReject(quiz.id)}
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            onClick={() => handleDelete(quiz.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // Desktop: Table view
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Categoria</TableHead>
                          <TableHead>Dificuldade</TableHead>
                          <TableHead className="max-w-md">Questão</TableHead>
                          <TableHead>Pontos</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {draftQuizzes.map((quiz) => (
                          <TableRow key={quiz.id}>
                            <TableCell>
                              <Badge variant="outline">
                                {categoryLabels[quiz.category] || quiz.category}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={
                                  quiz.difficulty === "beginner" ? "secondary" :
                                  quiz.difficulty === "intermediate" ? "default" : "destructive"
                                }
                              >
                                {difficultyLabels[quiz.difficulty] || quiz.difficulty}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-md truncate">
                              {quiz.question}
                            </TableCell>
                            <TableCell>{quiz.points}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedQuiz(quiz);
                                    setPreviewOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-green-600 hover:text-green-700"
                                  onClick={() => handleApprove(quiz.id)}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleReject(quiz.id)}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-muted-foreground hover:text-destructive"
                                  onClick={() => handleDelete(quiz.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approved">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Questões Aprovadas</CardTitle>
                <CardDescription className="text-sm">
                  Questões publicadas e disponíveis para os usuários
                </CardDescription>
              </CardHeader>
              <CardContent>
                {approvedQuizzes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhuma questão aprovada ainda.
                  </p>
                ) : isMobile ? (
                  // Mobile: Cards view
                  <div className="space-y-3">
                    {approvedQuizzes.slice(0, 50).map((quiz) => (
                      <Card key={quiz.id} className="p-4">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {categoryLabels[quiz.category] || quiz.category}
                          </Badge>
                          <Badge 
                            variant={
                              quiz.difficulty === "beginner" ? "secondary" :
                              quiz.difficulty === "intermediate" ? "default" : "destructive"
                            }
                            className="text-xs"
                          >
                            {difficultyLabels[quiz.difficulty] || quiz.difficulty}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">{quiz.points} pts</Badge>
                        </div>
                        <p className="text-sm line-clamp-2 mb-3">{quiz.question}</p>
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedQuiz(quiz);
                              setPreviewOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // Desktop: Table view
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Categoria</TableHead>
                          <TableHead>Dificuldade</TableHead>
                          <TableHead className="max-w-md">Questão</TableHead>
                          <TableHead>Pontos</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {approvedQuizzes.slice(0, 50).map((quiz) => (
                          <TableRow key={quiz.id}>
                            <TableCell>
                              <Badge variant="outline">
                                {categoryLabels[quiz.category] || quiz.category}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={
                                  quiz.difficulty === "beginner" ? "secondary" :
                                  quiz.difficulty === "intermediate" ? "default" : "destructive"
                                }
                              >
                                {difficultyLabels[quiz.difficulty] || quiz.difficulty}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-md truncate">
                              {quiz.question}
                            </TableCell>
                            <TableCell>{quiz.points}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedQuiz(quiz);
                                  setPreviewOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Pré-visualização da Questão</DialogTitle>
              <DialogDescription>
                Revise todos os detalhes antes de aprovar
              </DialogDescription>
            </DialogHeader>
            
            {selectedQuiz && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Badge variant="outline">
                    {categoryLabels[selectedQuiz.category] || selectedQuiz.category}
                  </Badge>
                  <Badge>
                    {difficultyLabels[selectedQuiz.difficulty] || selectedQuiz.difficulty}
                  </Badge>
                  <Badge variant="secondary">{selectedQuiz.points} pts</Badge>
                </div>

                {selectedQuiz.lesson_content && (
                  <div>
                    <Label className="text-sm font-medium">Conteúdo Educacional</Label>
                    <div className="mt-1 p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap">
                      {selectedQuiz.lesson_content}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-sm font-medium">Pergunta</Label>
                  <p className="mt-1 font-medium">{selectedQuiz.question}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium">Alternativas</Label>
                  <div className="mt-1 space-y-2">
                    {Object.entries(selectedQuiz.options).map(([key, value]) => (
                      <div 
                        key={key}
                        className={`p-2 rounded border ${
                          key === selectedQuiz.correct_answer 
                            ? "border-green-500 bg-green-50 dark:bg-green-950" 
                            : "border-border"
                        }`}
                      >
                        <span className="font-medium">{key.toUpperCase()})</span> {value}
                        {key === selectedQuiz.correct_answer && (
                          <Badge className="ml-2 bg-green-500">Correta</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">Explicação</Label>
                  <p className="mt-1 text-muted-foreground">{selectedQuiz.explanation}</p>
                </div>

                {selectedQuiz.status === "draft" && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      className="flex-1"
                      onClick={() => {
                        handleApprove(selectedQuiz.id);
                        setPreviewOpen(false);
                      }}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Aprovar
                    </Button>
                    <Button 
                      variant="destructive"
                      className="flex-1"
                      onClick={() => {
                        handleReject(selectedQuiz.id);
                        setPreviewOpen(false);
                      }}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Rejeitar
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </CandidateLayout>
  );
}
